/*
 * Display projection of OPS ClubFeature and /addons/runtime evidence.
 * Registration, activation, operational health, authorization and release policy
 * are independent. This module never grants rights or provisions a feature.
 */
export const MemberFeatureState = Object.freeze({
  READY: "ready",
  DISABLED: "disabled",
  UNREGISTERED: "unregistered",
  RUNTIME_MISSING: "runtime_missing",
  NOT_CONFIGURED: "not_configured",
  UNHEALTHY: "unhealthy",
  DEPENDENCY_BLOCKED: "dependency_blocked",
  NOT_READY: "not_ready",
  UNAUTHORIZED: "unauthorized",
  RELEASE_BLOCKED: "release_blocked",
  UNVERIFIED: "unverified",
});

export const MemberFeatureLabels = Object.freeze({
  ready: "Technisch bereit",
  disabled: "Für diesen Club deaktiviert",
  unregistered: "Club-Feature nicht registriert",
  runtime_missing: "Member-Addon nicht aktiv oder Feature nicht installiert",
  not_configured: "Konfiguration fehlt",
  unhealthy: "Laufzeit oder Feature nicht gesund",
  dependency_blocked: "Erforderliche Dienste nicht bereit",
  not_ready: "Betriebsbereitschaft nicht bestätigt",
  unauthorized: "Berechtigung fehlt",
  release_blocked: "Release-Freigabe fehlt",
  unverified: "Betriebsbereitschaft nicht verifiziert",
});

const bool = (value) => typeof value === "boolean" ? value : null;
const validKey = (key) => typeof key === "string" && !!key.trim();
const allVerified = (values) => values.some((value) => value === false) ? false
  : values.some((value) => value !== true) ? null : true;

/** Only feature_key identifies a registration. Never interpret capability_key. */
export function indexClubFeatures(rows) {
  if (!Array.isArray(rows)) return null;
  const result = new Map();
  for (const row of rows) {
    if (!row || !validKey(row.feature_key)) continue;
    // Duplicate keys are ambiguous evidence, not a last-write-wins shortcut.
    result.set(row.feature_key, result.has(row.feature_key) ? null : row);
  }
  return result;
}

function dependencyEvidence(runtime, index, featureKey, serviceReadiness, expectedClubId) {
  const states = runtime?.required_shared_services;
  const dependencies = Array.isArray(states) ? states.map((service) => {
    const required = service?.required !== false;
    const healthy = bool(service?.healthy);
    const available = typeof service?.status === "string" ? service.status === "available" : null;
    return {
      serviceKey: typeof service?.service_key === "string" ? service.service_key : "",
      required,
      healthy,
      available,
      ready: !validKey(service?.service_key) ? null : allVerified([healthy, available]),
    };
  }) : [];
  const aggregate = bool(runtime?.services_ready);
  const requiredStates = dependencies.filter((service) => service.required).map((service) => service.ready);
  requiredStates.push(Array.isArray(states) ? aggregate : null);

  // Provider guarantees come from the real, club-scoped readiness endpoint.
  // They are deliberately not inferred for unrelated features or from a flag.
  const providerChecks = featureKey === "push"
    ? ["push_available", "push_delegated_tokens", "push_idempotent_delivery", "push_dead_receipts"]
    : featureKey === "community_chat"
      ? ["realtime_available", "realtime_resume", "realtime_signed_tickets", "realtime_stale_sessions"]
      : [];
  const scoped = serviceReadiness?.schema_version === 1 && expectedClubId != null
    && serviceReadiness.club_id != null
    && String(serviceReadiness.club_id) === String(expectedClubId)
    && Array.isArray(serviceReadiness.checks);
  for (const checkKey of providerChecks) {
    const checks = scoped ? serviceReadiness.checks.filter((check) => check?.key === checkKey) : [];
    const ready = checks.length === 1 ? bool(checks[0].ready) : null;
    dependencies.push({ serviceKey: checkKey, required: true, ready, source: "club_service_readiness" });
    requiredStates.push(ready);
  }

  // Creating a table-game session explicitly requires game_controller in the
  // existing router. A registered/enabled game controller is still not ready.
  if (featureKey === "table_games") {
    const controller = index?.get("game_controller");
    const ready = controller ? allVerified(["enabled", "installed", "configured", "healthy", "ready"]
      .map((field) => bool(controller[field]))) : null;
    dependencies.push({ featureKey: "game_controller", required: true, ready, source: "club_feature" });
    requiredStates.push(ready);
  }
  return { dependenciesReady: allVerified(requiredStates), dependencies };
}

/**
 * Operational readiness only; READY is not capability or Gate B authorization.
 * addonRuntime is the matched lenox.memberapp item from a freshly scope-checked
 * runtime response. The bridge must verify the envelope before passing it here.
 * ClubFeature.ready and every underlying dimension require explicit server
 * evidence. In particular enabled=true alone always remains UNVERIFIED.
 * expectedClubId binds optional serviceReadiness to the active club.
 */
export function resolveClubFeatureState({
  featureKey, clubFeatures, loaded = true, addonRuntime, serviceReadiness, expectedClubId,
}) {
  const index = loaded === true && validKey(featureKey) ? indexClubFeatures(clubFeatures) : null;
  const registered = index ? index.has(featureKey) : null;
  const row = registered ? index.get(featureKey) : null;
  const runtime = addonRuntime?.addon_id === "lenox.memberapp" ? addonRuntime : null;
  const runtimeEnabled = bool(runtime?.enabled);
  const runtimeActive = runtime && typeof runtime.status === "string"
    ? (runtime.status !== "active" || runtimeEnabled === false ? false
      : runtimeEnabled === true ? true : null) : null;
  const evidence = {
    featureKey: validKey(featureKey) ? featureKey : "",
    registered,
    enabled: bool(row?.enabled),
    installed: bool(row?.installed),
    configured: bool(row?.configured),
    healthy: bool(row?.healthy),
    rowReady: bool(row?.ready),
    runtimeActive,
    runtimeHealthy: bool(runtime?.healthy),
    runtimeReady: bool(runtime?.ready),
    ...dependencyEvidence(runtime, index, featureKey, serviceReadiness, expectedClubId),
  };
  const dimensions = [evidence.registered, evidence.enabled, evidence.installed,
    evidence.configured, evidence.healthy, evidence.rowReady, runtimeActive,
    evidence.runtimeHealthy, evidence.runtimeReady, evidence.dependenciesReady];
  const unverified = dimensions.some((value) => value === null);
  const result = (state, reason) => ({
    ...evidence,
    state,
    label: loaded !== true ? "Club-Features werden geladen" : MemberFeatureLabels[state],
    ready: state === MemberFeatureState.READY,
    unverified,
    reasons: reason ? [reason] : [],
  });

  if (!index) return result(MemberFeatureState.UNVERIFIED, "club_features_unverified");
  if (!registered) return result(MemberFeatureState.UNREGISTERED, "registration_missing");
  if (!row) return result(MemberFeatureState.UNVERIFIED, "ambiguous_registration");
  if (evidence.enabled === false) return result(MemberFeatureState.DISABLED, "club_feature_disabled");
  if (evidence.installed === false || runtimeActive === false) {
    return result(MemberFeatureState.RUNTIME_MISSING, "runtime_or_installation_missing");
  }
  if (evidence.configured === false) return result(MemberFeatureState.NOT_CONFIGURED, "configuration_missing");
  if (evidence.healthy === false || evidence.runtimeHealthy === false) {
    return result(MemberFeatureState.UNHEALTHY, "health_failed");
  }
  if (evidence.dependenciesReady === false) return result(MemberFeatureState.DEPENDENCY_BLOCKED, "required_service_failed");
  if (evidence.rowReady === false || evidence.runtimeReady === false) {
    return result(MemberFeatureState.NOT_READY, "server_readiness_false");
  }
  if (unverified) return result(MemberFeatureState.UNVERIFIED, "readiness_evidence_missing");
  return result(MemberFeatureState.READY);
}

/**
 * Full availability additionally requires explicit OPS authorization and release
 * policy. A role hint, feature flag or healthy installation cannot replace either.
 * runtimeActive is retained for API compatibility, but is never readiness proof.
 */
export function resolveMemberFeatureAvailability(context) {
  const operational = resolveClubFeatureState(context);
  const authorized = bool(context.authorized);
  const releaseAllowed = bool(context.releaseAllowed);
  let state = operational.state;
  if (context.runtimeActive === false) state = MemberFeatureState.RUNTIME_MISSING;
  else if (authorized === false) state = MemberFeatureState.UNAUTHORIZED;
  else if (releaseAllowed === false) state = MemberFeatureState.RELEASE_BLOCKED;
  else if (authorized !== true || releaseAllowed !== true) state = MemberFeatureState.UNVERIFIED;
  return {
    ...operational,
    state,
    label: MemberFeatureLabels[state],
    authorized,
    releaseAllowed,
    // `ready` remains operational; `available` also includes authorization/policy.
    available: state === MemberFeatureState.READY,
  };
}

/** Classifies definitions without registering, enabling or saving any feature. */
export function resolveMemberFeatureCatalog(definitions, context) {
  if (!Array.isArray(definitions)) return [];
  return definitions.map((definition) => ({
    ...definition,
    availability: resolveMemberFeatureAvailability({ ...context, featureKey: definition?.feature_key }),
  }));
}
